import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { WebStoreProductRedemptionTemplate } from '../../components/Templates';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import useDoWhile, { DoWhileFn } from '../../hooks/useDoWhile';
import useLocale from '../../hooks/useLocale';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import {
  RewardRedemption,
  RewardRedemptionState,
  RewardRedemptionType,
  RewardReferralRole,
} from '../../models/RewardStore';
import { useDraftStore } from '../../stores/DraftStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { capitalizeFirst } from '../../utils/string';

const { getCurrentUser } = useUserStore.getState();
const { getRedemption, fetchRedemption, captureRedemptionPayment } =
  useRewardStore.getState();

type State = {
  rewardRedemption: RewardRedemption | undefined | null;
  isLoading: boolean;
  isNavigateHome: boolean;
  referralRole: RewardReferralRole | undefined;
};

const { fetchOffer } = useRewardStore.getState();

const WebStoreProductRedemptionScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const l = useLocale();
  const { rewardOfferId, rewardRedemptionId } = useParams();
  const query = new URLSearchParams(useLocation().search);
  // const success = query.get('success') === 'true';
  const userRole = query.get('userRole');
  const userId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));
  const webBoosterType = userAccount?.settings.webBoosterType;

  const referralRoleEnum =
    RewardReferralRole[
      capitalizeFirst(userRole ?? '') as keyof typeof RewardReferralRole
    ];

  const [state, setState] = useState<State>({
    rewardRedemption: undefined,
    isLoading: false,
    isNavigateHome: false,
    referralRole: undefined,
  });

  useRedirectEntry();

  useEffect(() => {
    if (referralRoleEnum) {
      switch (referralRoleEnum) {
        case RewardReferralRole.Customer: {
          setState((s) => ({
            ...s,
            referralRole: RewardReferralRole.Customer,
          }));
          break;
        }
        case RewardReferralRole.Partner: {
          setState((s) => ({
            ...s,
            referralRole: RewardReferralRole.Partner,
          }));
          break;
        }
        case RewardReferralRole.Distributor: {
          setState((s) => ({
            ...s,
            referralRole: RewardReferralRole.Distributor,
          }));
          break;
        }
        default: {
          setState((s) => ({ ...s, isNavigateHome: true }));
        }
      }
    } else {
      setState((s) => ({ ...s, isNavigateHome: true }));
    }
  }, [referralRoleEnum]);

  useRedirectHome(state.isNavigateHome);

  const rewardOfferStore = 'system';

  useRewardStoreListField('offers', `${rewardOfferStore}_${rewardOfferId}`);

  useEffect(() => {
    if (rewardOfferId) {
      fetchOffer(rewardOfferStore, rewardOfferId).catch(console.error);
    }
  }, [rewardOfferId]);

  const rewardOffer = useRewardStore(
    (store) => store.getOffer(rewardOfferId ?? ''),
    shallow,
  );

  const i18nRewardOfferInfo = l(rewardOffer?.i18nRewardOfferInfo);

  const loadRedemption = useDoWhile(
    useCallback<DoWhileFn>(
      async (signal) => {
        if (rewardRedemptionId !== undefined) {
          let rewardRedemption = await fetchRedemption(
            rewardOfferStore,
            rewardRedemptionId,
            { signal },
          );

          // capture paypal payment
          if (
            rewardRedemption?.state === RewardRedemptionState.Paying &&
            rewardRedemption?.payment?.gateway === 'paypal' &&
            !rewardRedemption.payment.subscriptionId
          ) {
            await captureRedemptionPayment(
              rewardOfferStore,
              rewardRedemptionId,
              { signal },
            );
            rewardRedemption =
              getRedemption(rewardRedemptionId) ?? rewardRedemption;
          }

          // success
          if (rewardRedemption?.state === RewardRedemptionState.Fulfilled) {
            if (
              rewardRedemption.reward?.type === RewardRedemptionType.WebPlan
            ) {
              await getCurrentUser({ signal });
            }
            setState((s) => ({
              ...s,
              rewardRedemption,
              isLoading: false,
            }));
            return true;
          }

          // error
          if (rewardRedemption?.state !== RewardRedemptionState.Paying) {
            setState((s) => ({
              ...s,
              rewardRedemption: null,
              isLoading: false,
            }));
            return true;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 3000));
        return false;
      },
      [rewardRedemptionId],
    ),
  );

  useEffect(() => {
    if (rewardRedemptionId && userId) {
      setState((s) => ({ ...s, isLoading: true }));
      loadRedemption().catch(console.error);
    }
  }, [loadRedemption, rewardRedemptionId, userId]);

  useRedirectHome(state.rewardRedemption === null);

  const onClickProduct = useCallback(() => {
    if (
      state.rewardRedemption?.reward?.type === RewardRedemptionType.ReferralRole
    ) {
      navigate('/store/role');
    } else if (
      state.rewardRedemption?.reward?.type ===
      RewardRedemptionType.ClassroomMembershipTierSet
    ) {
      const classroomId = state.rewardRedemption.reward.classroomId;
      navigate(`/club/${classroomId}`);
    } else if (
      state.rewardRedemption?.reward?.type === RewardRedemptionType.Bread
    ) {
      navigate(
        getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Home) + '#donate-bread',
      );
    } else if (
      state.rewardRedemption?.reward?.type !== RewardRedemptionType.Token
    ) {
      navigate(getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Home));
    } else if (useDraftStore.getState().getGameProducts().length > 0) {
      navigate('/create/memory-game/select-games');
    } else if (useDraftStore.getState().getGameContents().length > 0) {
      navigate('/create/memory-game/input-content');
    } else {
      navigate(getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Home));
    }
  }, [navigate, state.rewardRedemption?.reward]);

  const onClickWebStore = useCallback(() => {
    navigate('/store');
  }, [navigate]);

  const productName = useMemo(() => {
    if (state.rewardRedemption?.reward?.type === RewardRedemptionType.Token) {
      return t('AI Revision Club');
    } else {
      return i18nRewardOfferInfo?.name;
    }
  }, [i18nRewardOfferInfo?.name, state.rewardRedemption?.reward?.type, t]);

  return (
    <>
      {state.referralRole && (
        <WebStoreProductRedemptionTemplate
          state={state.rewardRedemption?.state ?? RewardRedemptionState.Paying}
          rewardType={state.rewardRedemption?.reward?.type}
          referralRole={state.referralRole}
          productName={productName}
          webBoosterType={webBoosterType}
          onClickProduct={onClickProduct}
          onClickWebStore={onClickWebStore}
        />
      )}
    </>
  );
};

export default WebStoreProductRedemptionScreen;
